import { useState } from "react";
import { useCreateBay } from "../../hooks/useGreenhouseBays";
import type { GreenhouseBay } from "../../services/greenhouseBaysApi";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Plus } from "lucide-react";

interface CreateGreenhouseBayDialogProps {
  greenhouseId: string;
  greenhouseSize: number;
  existingBays: GreenhouseBay[];
}

const initialFormData = {
  bayName: "",
  description: "",
  sizeSquareMeters: "",
  bedCount: "",
  trayCapacity: "",
};

export const CreateGreenhouseBayDialog = ({
  greenhouseId,
  greenhouseSize,
  existingBays,
}: CreateGreenhouseBayDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const createMutation = useCreateBay();
  const confirm = useConfirm();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const baySize = parseFloat(formData.sizeSquareMeters);
    
    // Validation: Bay size cannot exceed greenhouse size
    if (baySize > greenhouseSize) {
      setFieldErrors({
        sizeSquareMeters: [`Bay size cannot exceed greenhouse size of ${greenhouseSize}m²`],
      });
      return;
    }

    // Validation: Total bay sizes cannot exceed greenhouse size
    const totalExistingSize = existingBays.reduce((sum, bay) => sum + bay.sizeSquareMeters, 0);
    if (totalExistingSize + baySize > greenhouseSize) {
      const availableSpace = greenhouseSize - totalExistingSize;
      setFieldErrors({
        sizeSquareMeters: [`Available space in greenhouse is ${availableSpace.toFixed(2)}m². Current bays use ${totalExistingSize.toFixed(2)}m².`],
      });
      return;
    }

    const payload = {
      greenhouseId,
      bayName: formData.bayName,
      description: formData.description,
      sizeSquareMeters: baySize,
      bedCount: parseInt(formData.bedCount),
      trayCapacity: parseInt(formData.trayCapacity),
    };

    confirm({
      title: "Create bay?",
      description: `“${formData.bayName}” will be added to this greenhouse.`,
      confirmLabel: "Create bay",
      onConfirm: () =>
        withConfirmSuccess(createMutation.mutateAsync(payload), "Bay created")
          .then(() => {
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" size="sm">
          <Plus className="w-4 h-4" />
          Create Bay
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Bay</DialogTitle>
          <DialogDescription>Add a new bay to this greenhouse</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {createMutation.error.message}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="bayName">
              Bay Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="bayName"
              placeholder="e.g., Bay A"
              value={formData.bayName}
              onChange={(e) => handleChange("bayName", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.bayName && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.bayName.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <Textarea
              id="description"
              placeholder="Bay details..."
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={createMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="sizeSquareMeters">
              Size (m²) <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="sizeSquareMeters"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.sizeSquareMeters}
              onChange={(e) => handleChange("sizeSquareMeters", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.sizeSquareMeters && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.sizeSquareMeters.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="bedCount">
              Bed Count <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="bedCount"
              type="number"
              placeholder="0"
              value={formData.bedCount}
              onChange={(e) => handleChange("bedCount", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.bedCount && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.bedCount.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="trayCapacity">
              Tray Capacity <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="trayCapacity"
              type="number"
              placeholder="0"
              value={formData.trayCapacity}
              onChange={(e) => handleChange("trayCapacity", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.trayCapacity && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.trayCapacity.join(", ")}
              </div>
            )}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Bay"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};